import { useEffect, useId, useRef, useState } from "react";

import { useTheme } from "./ThemeProvider";

export function ThemePicker() {
  const { theme, themes, activeThemeIDs, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const buttonId = useId();
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click. Must run unconditionally — early-returning before
  // a hook violates Rules of Hooks when the active-set count changes.
  useEffect(() => {
    if (!open) {
      return;
    }
    const onClick = (event: MouseEvent) => {
      if (
        containerRef.current !== null &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  // A picker that surfaces only one option implies functionality that doesn't
  // exist. Hide entirely until at least two themes are active for this tenant.
  const switchable = themes.filter((option) => activeThemeIDs.has(option.id));
  if (switchable.length <= 1) {
    return null;
  }

  return (
    <div className="theme-picker" ref={containerRef}>
      <button
        type="button"
        className="theme-picker__trigger"
        id={buttonId}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="theme-picker__swatch" aria-hidden="true" />
        <span className="theme-picker__trigger-label">{theme.name}</span>
      </button>
      {open && (
        <div className="theme-picker__menu" role="menu" id={menuId} aria-labelledby={buttonId}>
          {switchable.map((option) => {
            const active = option.id === theme.id;
            return (
              <button
                key={option.id}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                className={`theme-picker__option${active ? " is-active" : ""}`}
                onClick={() => {
                  setTheme(option.id);
                  setOpen(false);
                }}
                title={option.tagline}
              >
                <span className="theme-picker__option-name">{option.name}</span>
                <span className="theme-picker__option-tag">{option.tagline}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
